import { label, projectDate } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { Button } from '@shared/ui/Button';
import { Card, Muted, Notice, Small } from '../styles';
import { choice, optional, textField } from '../types';
import { PagedTable } from './CollectionTable';

export interface ImportCardProps {
  imported: KspWorkspace['imports'][number];
  token?: string | null;
  timezone: string;
  editable: boolean;
  versionId?: string | null;
  loadingImport: boolean;
  detailedImportId: string;
  importError: unknown;
  workOptions: { value: string; label: string }[];
  calendarOptions: { value: string; label: string }[];
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  fail: (error: unknown) => void;
  setDetailedImportId: (id: string) => void;
}

export function ImportCard({
  imported,
  token,
  timezone,
  editable,
  versionId,
  loadingImport,
  detailedImportId,
  importError,
  workOptions,
  calendarOptions,
  permission,
  open,
  execute,
  fail,
  setDetailedImportId,
}: ImportCardProps) {
  return (
    <Card key={imported.id}>
      <h3>{imported.original_name}</h3>
      <Muted>
        {label(imported.status)} · {projectDate(imported.created_at, timezone)}
      </Muted>
      <Small title={`SHA-256 ${imported.file_sha256}`}>Оригинал файла сохранён без изменений</Small>
      {imported.source_file_id && (
        <Button
          $variant="ghost"
          onClick={async () => {
            try {
              const result = await downloadAuthorizedFile(
                `/api/v1/agents/ksp/files/${imported.source_file_id}`,
                { accessToken: token, fallbackName: imported.original_name },
              );
              if (!result.ok)
                fail(new Error('Исходный файл не загружен. Повторите скачивание.'));
            } catch (error) {
              fail(error);
            }
          }}
        >
          Скачать сохранённый оригинал
        </Button>
      )}
      {(imported.rows_truncated || imported.issues_truncated) && (
        <Notice>
          Предварительный просмотр: показано {imported.rows.length} из{' '}
          {imported.row_count ?? 'неизвестного числа'} строк и {imported.issues.length} из{' '}
          {imported.issue_count ?? 'неизвестного числа'} замечаний.
          <Button
            $variant="secondary"
            disabled={loadingImport}
            onClick={() => setDetailedImportId(imported.id)}
          >
            Открыть полный разбор для проверки
          </Button>
        </Notice>
      )}
      {Boolean(importError) && detailedImportId === imported.id && (
        <Notice $error>
          Полный разбор не загружен. Предварительный просмотр не заменяет проверку всего файла.
        </Notice>
      )}
      <Button
        disabled={
          !editable ||
          !versionId ||
          imported.rows_truncated ||
          imported.issues_truncated ||
          !['confirmed', 'accepted'].includes(imported.status)
        }
        onClick={() =>
          open(
            'Применить проверенные строки графика',
            [textField('basis', 'Основание проверки и сопоставления')],
            (values) =>
              execute(`imports/${imported.id}/decisions`, {
                ...values,
                operation: 'apply',
                version_id: versionId,
              }),
          )
        }
      >
        Применить в выбранную версию
      </Button>
      {imported.issues.map((issue, i) => (
        <Notice $error key={i}>
          {issue.code} · {issue.sheet}!{issue.cell ?? issue.row} · {issue.message ?? issue.reason}
        </Notice>
      ))}
      <PagedTable
        rows={imported.rows}
        columns={[
          {
            name: 'Строка и источник',
            value: (row) => (
              <span title={row.source_ref}>
                {row.sheet}, строка {row.row}
              </span>
            ),
          },
          {
            name: 'Извлечённые значения',
            value: (row) =>
              Object.entries(row.values).map(([key, value]) => (
                <Small key={key}>
                  {label(key)}: {value ?? 'не определено'}
                </Small>
              )),
          },
          {
            name: 'Решение',
            value: (row) => (
              <Button
                $size="sm"
                disabled={!permission() || imported.status === 'applied'}
                onClick={() =>
                  open(
                    'Проверить импортную строку',
                    [
                      choice('decision', 'Решение', [
                        { value: 'accepted', label: 'Принять проверенную строку' },
                        { value: 'rejected', label: 'Отклонить строку для применения' },
                      ]),
                      choice(
                        'entity',
                        'Тип импортной строки',
                        [
                          { value: 'activity', label: 'Работа' },
                          { value: 'calendar', label: 'Календарь' },
                          { value: 'wbs', label: 'WBS' },
                          { value: 'dependency', label: 'Связь' },
                          { value: 'resource', label: 'Ресурс' },
                          { value: 'rd', label: 'Объём РД' },
                          { value: 'supply', label: 'Поставка' },
                          { value: 'milestone', label: 'Веха' },
                          { value: 'actual', label: 'Факт для проверки' },
                        ],
                        (
                          {
                            Календари: 'calendar',
                            WBS: 'wbs',
                            Связи: 'dependency',
                            Ресурсы: 'resource',
                            'Объёмы РД': 'rd',
                            Поставки: 'supply',
                            Вехи: 'milestone',
                            Факт: 'actual',
                          } as Record<string, string>
                        )[row.sheet] ?? 'activity',
                      ),
                      {
                        ...textField(
                          'reviewed_fields',
                          'Проверенные поля строки (JSON)',
                          true,
                          JSON.stringify(row.values, null, 2),
                        ),
                        type: 'textarea',
                      },
                      {
                        ...choice(
                          'activity_id',
                          'Работа для сопоставления (при обновлении)',
                          workOptions,
                        ),
                        required: false,
                      },
                      textField(
                        'code',
                        'Проверенный код работы',
                        false,
                        row.values.code ?? '',
                      ),
                      textField(
                        'name',
                        'Проверенное наименование',
                        false,
                        row.values.name ?? '',
                      ),
                      {
                        ...choice(
                          'calendar_id',
                          'Проверенный календарь',
                          calendarOptions,
                          row.values.calendar_id ?? '',
                        ),
                        required: false,
                      },
                      {
                        ...textField(
                          'duration_minutes',
                          'Проверенная длительность, минуты',
                          false,
                          row.values.duration_minutes ?? '',
                        ),
                        type: 'number',
                      },
                      textField(
                        'quantity',
                        'Проверенное количество',
                        false,
                        row.values.quantity ?? '',
                      ),
                      textField('unit', 'Единица', false, row.values.unit ?? ''),
                      textField('comment', 'Основание решения'),
                    ],
                    (values) => {
                      const reviewed = JSON.parse(values.reviewed_fields) as Record<
                        string,
                        unknown
                      >;
                      return execute(`imports/${imported.id}/decisions`, {
                        decisions: [
                          {
                            ...reviewed,
                            ...optional('code', values.code),
                            ...optional('name', values.name),
                            ...optional('calendar_id', values.calendar_id),
                            ...optional('activity_id', values.activity_id),
                            ...optional('quantity', values.quantity),
                            ...optional('unit', values.unit),
                            ...(values.duration_minutes
                              ? { duration_minutes: Number(values.duration_minutes) }
                              : {}),
                            sheet: row.sheet,
                            row: row.row,
                            source_ref: row.source_ref,
                            decision: values.decision,
                            entity: values.entity,
                            comment: values.comment,
                          },
                        ],
                        version_id: versionId,
                      });
                    },
                  )
                }
              >
                Проверить
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
}
